import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { ConfirmModal } from "./ConfirmModal";

interface NavbarProps {
  /** "full": muestra links centrales (landing). "minimal": solo logo + acciones (Guia). */
  variant?: "full" | "minimal";
  /** Si true, el nav siempre es sólido (para páginas sin hero oscuro detrás) */
  alwaysSolid?: boolean;
}

const LINKS = [
  { href: "#como-funciona", label: "Cómo funciona" },
  { href: "#quienes-somos", label: "Sobre nosotros" },
  { href: "#temas", label: "Temas de interés" },
  { href: "/comunidad", label: "Comunidad" },
  { href: "#membresia", label: "Membresía" },
];

export function Navbar({ variant = "full", alwaysSolid = false }: NavbarProps) {
  const { isAuthenticated, user, clearSession } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(alwaysSolid);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (alwaysSolid) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [alwaysSolid]);

  function handleLogout() {
    clearSession();
    setConfirmLogout(false);
    navigate("/");
  }

  const solid = scrolled || alwaysSolid;
  const navClass = `navbar ${solid ? "navbar--solid" : "navbar--transparent navbar--dark-context"}`;

  return (
    <>
      <header className={navClass}>
        <div className="container navbar__inner">
          <Link to="/" className="navbar__logo">
            <img src="/logo-psicodosis.png" alt="PzicoDosis" />
            <span className="navbar__logo-text">PzicoDosis</span>
          </Link>

          {variant === "full" ? (
            <div className="navbar__links">
              {LINKS.map((l) =>
                l.href.startsWith("#") ? (
                  <a key={l.href} href={l.href} className="navbar__link">{l.label}</a>
                ) : (
                  <Link key={l.href} to={l.href} className="navbar__link">{l.label}</Link>
                )
              )}
              {isAuthenticated ? (
                <Link to="/dashboard" className="navbar__link" style={{ fontWeight: 700 }}>Mi espacio</Link>
              ) : (
                <Link to="/login" className="navbar__link" style={{ fontWeight: 600 }}>Entrar</Link>
              )}
              <Link to={isAuthenticated ? "/dashboard" : "/registro"} className="btn btn-dark btn--sm">
                {isAuthenticated ? "Mi espacio →" : "Únete gratis"}
              </Link>
            </div>
          ) : (
            <div className="navbar__actions" style={{ display: "flex" }}>
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary btn--sm">Mi espacio →</Link>
              ) : (
                <>
                  <Link to="/login" className="navbar__login">Entrar</Link>
                  <Link to="/registro" className="btn btn-primary btn--sm">Únete gratis</Link>
                </>
              )}
            </div>
          )}

          {variant === "full" && (
            <button
              className="navbar__hamburger"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu size={22} />
            </button>
          )}
        </div>
      </header>

      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Cerrar menú">
              <X size={18} />
            </button>
            {variant === "full" &&
              LINKS.map((l) =>
                l.href.startsWith("#") ? (
                  <a key={l.href} href={l.href} className="drawer-link" onClick={() => setDrawerOpen(false)}>{l.label}</a>
                ) : (
                  <Link key={l.href} to={l.href} className="drawer-link" onClick={() => setDrawerOpen(false)}>{l.label}</Link>
                )
              )}
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="drawer-link" onClick={() => setDrawerOpen(false)}>Mi espacio</Link>
                <Link to="/perfil" className="drawer-link" onClick={() => setDrawerOpen(false)}>👤 {user?.nombre.split(" ")[0]}</Link>
                <button onClick={() => { setDrawerOpen(false); setConfirmLogout(true); }} className="drawer-link" style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "#d99a93" }}>
                  Cerrar sesión
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="drawer-link" onClick={() => setDrawerOpen(false)}>Iniciar sesión</Link>
                <Link to="/registro" className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => setDrawerOpen(false)}>
                  Únete gratis →
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {confirmLogout && (
        <ConfirmModal
          title="¿Cerrar sesión?"
          message="Puedes volver a entrar cuando quieras. Tu progreso se mantiene guardado."
          confirmLabel="Cerrar sesión"
          danger
          onConfirm={handleLogout}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </>
  );
}
